// src/components/filters/FilterDashGlobal.jsx
import React, { useState, useEffect } from 'react';
import { Offcanvas, Form, Button } from 'react-bootstrap';
import { 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  Alert
} from '@mui/material';
import { ExpandLess, Storage, CloudQueue } from '@mui/icons-material';

const FilterDashGlobal = ({ show, handleClose, onApplyFilters }) => {
  const [filters, setFilters] = useState({
    sector: 'All',
    dateFrom: '',
    dateTo: '',
    company: '',
    local: '',
    comparison: 'All',
    budget: false,
    category: ['All']
  });

  // Estado para el modo mock/real
  const [useMockData, setUseMockData] = useState(() => {
    // Primero verificar localStorage, luego el .env
    const savedValue = localStorage.getItem('REACT_APP_USE_MOCK');
    if (savedValue !== null) {
      return savedValue === 'true';
    }
    return process.env.REACT_APP_USE_MOCK === 'true';
  });

  // Resetear filtros cuando se cierra el modal sin aplicar
  useEffect(() => {
    if (!show) {
      // Opcional: si quieres mantener los filtros cuando se cierra, comenta estas líneas
    }
  }, [show]);

  const handleSectorChange = (sector) => {
    setFilters({ ...filters, sector });
  };

  const handleComparisonChange = (comparison) => {
    setFilters({ ...filters, comparison });
  };

  const handleCategoryToggle = (category) => {
    let newCategories = [...filters.category];
    
    if (category === 'All') {
      newCategories = ['All'];
    } else {
      newCategories = newCategories.filter(c => c !== 'All');
      
      if (newCategories.includes(category)) {
        newCategories = newCategories.filter(c => c !== category);
      } else {
        newCategories.push(category);
      }
      
      if (newCategories.length === 0) {
        newCategories = ['All'];
      }
    }
    
    setFilters({ ...filters, category: newCategories });
  };

  const handleMockToggle = (event) => {
    const newValue = event.target.checked;
    setUseMockData(newValue);
    
    // Guardar en localStorage para persistencia entre sesiones
    localStorage.setItem('REACT_APP_USE_MOCK', newValue.toString());
    
    // Mostrar notificación o recargar si es necesario
    console.log(`Modo cambiado a: ${newValue ? 'MOCK' : 'REAL'}`);
    
    // Disparar un evento personalizado para notificar a otros componentes
    window.dispatchEvent(new CustomEvent('mockModeChanged', { 
      detail: { useMock: newValue } 
    }));
  };

  const handleApply = () => {
    // Enviar todos los filtros al componente padre
    onApplyFilters({
      ...filters,
      // Asegurar que los valores vacíos sean null
      company: filters.company || null,
      local: filters.local || null
    });
    handleClose();
  };

  const handleReset = () => {
    const resetFilters = {
      sector: 'All',
      dateFrom: '',
      dateTo: '',
      company: '',
      local: '',
      comparison: 'All',
      budget: false,
      category: ['All']
    };
    setFilters(resetFilters);
    
    // Aplicar el reset inmediatamente
    onApplyFilters({
      ...resetFilters,
      company: null,
      local: null
    });
  };

  const handleCancel = () => {
    // Cerrar sin aplicar cambios
    handleClose();
  };

  return (
    <Offcanvas show={show} onHide={handleCancel} placement="end" style={{ width: '400px' }}>
      <Offcanvas.Header closeButton className="border-bottom">
        <Offcanvas.Title className="fw-semibold">Filters</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="px-4">
        {/* Data Source Mode Switch */}
        <div className="mb-4 pb-4 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 fw-semibold">Data Source</h6>
            <ExpandLess fontSize="small" className="text-muted" />
          </div>
          
          <div className="bg-light p-3 rounded">
            <FormControlLabel
              control={
                <Switch
                  checked={useMockData}
                  onChange={handleMockToggle}
                  color="warning"
                />
              }
              label={
                <div className="d-flex align-items-center gap-2">
                  {useMockData ? (
                    <>
                      <Storage fontSize="small" />
                      <span className="fw-medium">Mock Data</span>
                    </>
                  ) : (
                    <>
                      <CloudQueue fontSize="small" />
                      <span className="fw-medium">Real Backend</span>
                    </>
                  )}
                </div>
              }
            />
            
            <Alert 
              severity={useMockData ? "info" : "success"} 
              variant="outlined"
              className="mt-2 mb-0"
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              {useMockData 
                ? "Using fictitious data for testing" 
                : "Connected to production backend"}
            </Alert>
          </div>
        </div>

        {/* Sector Section */}
        <div className="mb-4 pb-4 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 fw-semibold">Sector</h6>
            <ExpandLess fontSize="small" className="text-muted" />
          </div>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['All', 'Online', 'Events'].map((sector) => (
              <Chip
                key={sector}
                label={sector}
                onClick={() => handleSectorChange(sector)}
                color={filters.sector === sector ? 'warning' : 'default'}
                variant={filters.sector === sector ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </div>

        {/* Dates Section */}
        <div className="mb-4 pb-4 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 fw-semibold">Dates</h6>
            <ExpandLess fontSize="small" className="text-muted" />
          </div>
          <div className="row g-2">
            <div className="col-6">
              <TextField
                label="From"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
            </div>
            <div className="col-6">
              <TextField
                label="To"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Company Section */}
        <div className="mb-4 pb-4 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 fw-semibold">Company</h6>
            <ExpandLess fontSize="small" className="text-muted" />
          </div>
          <FormControl fullWidth size="small">
            <InputLabel>Select here</InputLabel>
            <Select
              value={filters.company}
              label="Select here"
              onChange={(e) => setFilters({ ...filters, company: e.target.value })}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="1">Company 1</MenuItem>
              <MenuItem value="2">Company 2</MenuItem>
              <MenuItem value="3">Company 3</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Local Section */}
        <div className="mb-4 pb-4 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 fw-semibold">Local</h6>
            <ExpandLess fontSize="small" className="text-muted" />
          </div>
          <FormControl fullWidth size="small">
            <InputLabel>Select here</InputLabel>
            <Select
              value={filters.local}
              label="Select here"
              onChange={(e) => setFilters({ ...filters, local: e.target.value })}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="1">Local 1</MenuItem>
              <MenuItem value="2">Local 2</MenuItem>
              <MenuItem value="3">Local 3</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Comparison Section */}
        <div className="mb-4 pb-4 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 fw-semibold">Comparison</h6>
            <ExpandLess fontSize="small" className="text-muted" />
          </div>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap className="mb-3">
            {['All', 'Previous Month', 'Previous Year'].map((comparison) => (
              <Chip
                key={comparison}
                label={comparison}
                onClick={() => handleComparisonChange(comparison)}
                color={filters.comparison === comparison ? 'warning' : 'default'}
                variant={filters.comparison === comparison ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Stack>
          
          <Form.Check 
            type="checkbox"
            label="Budget"
            checked={filters.budget}
            onChange={(e) => setFilters({ ...filters, budget: e.target.checked })}
          />
        </div>

        {/* Category Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 fw-semibold">Category</h6>
            <ExpandLess fontSize="small" className="text-muted" />
          </div>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['All', 'Food & Beverage', 'Merchandising', 'Marketing'].map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryToggle(category)}
                color={filters.category.includes(category) ? 'warning' : 'default'}
                variant={filters.category.includes(category) ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Stack>
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2 mt-4">
          <Button 
            variant="outline-secondary" 
            onClick={handleReset}
            className="flex-fill"
          >
            Reset
          </Button>
          <Button 
            variant="warning" 
            onClick={handleApply}
            className="flex-fill text-white"
          >
            Apply Filters
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default FilterDashGlobal;